import React, { createContext, useContext, useRef } from 'react';
import { useProjectDirectory } from '../hooks/useProjectDirectory';
import { useProjectVersionData } from '../hooks/useProjectVersionData';
import { useProjectAutoLocator } from '@/hooks/useProjectAutoLocator';
import { useProjectDirectories } from '@/context/ProjectDirectoriesContext';
import { useOpenProjectInDAW } from '../hooks/useOpenProjectInDAW';
import { useVersionSelection } from '../hooks/useVersionSelection';
import { detectPrimaryDAW } from '@/common/utils/fileUtils';
import type { FilesTabRef } from '../tabs/FileExplorerTab';
import {
  useGetProjectQuery,
  type GetProjectQueryData,
  type ProjectFileType,
} from '@/generated/graphql';
import type { DAWType } from '@/types/daw';

type ProjectDetails = GetProjectQueryData['project'];

interface ProjectDetailsContextValue {
  projectId: string;
  project: ProjectDetails | undefined;
  projectLoading: boolean;
  projectError: unknown;
  refetchProject: () => void;
  projectDirHandle: FileSystemDirectoryHandle | null;
  projectDirPath: string | null;
  syncProjectDir: string | FileSystemDirectoryHandle | null;
  latestVersionFiles: ProjectFileType[];
  latestVersionLoading: boolean;
  latestVersionError: unknown;
  primaryDAW: { type: DAWType } | null;
  handleLocateProject: () => Promise<void>;
  handleAutoLocateProject: () => Promise<void>;
  handleChangeProjectLocation: () => Promise<void>;
  handleResetProjectLocation: () => void;
  handleOpenProject: () => Promise<void>;
  filesWithSyncRef: React.RefObject<FilesTabRef | null>;
  onProjectUpdated: () => void;
  /** The version number currently selected in the shared version selector */
  selectedVersion: number;
  setSelectedVersion: (v: number) => void;
  latestVersionNumber: number;
}

const ProjectDetailsContext = createContext<ProjectDetailsContextValue | null>(
  null
);

interface ProjectDetailsProviderProps {
  projectId: string;
  onProjectUpdated: () => void;
  children: React.ReactNode;
}

export function ProjectDetailsProvider({
  projectId,
  onProjectUpdated,
  children,
}: ProjectDetailsProviderProps) {
  const filesWithSyncRef = useRef<FilesTabRef>(null);

  const {
    handle: projectDirHandle,
    path: projectDirPath,
    syncDir: syncProjectDir,
  } = useProjectDirectory(projectId);

  const {
    data: projectData,
    loading: projectLoading,
    error: projectError,
    refetch: refetchProject,
  } = useGetProjectQuery({
    variables: { projectId },
  });

  const project = projectData?.project;

  const {
    latestVersionFiles,
    loading: latestVersionLoading,
    error: latestVersionError,
  } = useProjectVersionData(project);

  const { selectedVersion, setSelectedVersion, latestVersionNumber } =
    useVersionSelection(project);
  const { removeProjectDirectory } = useProjectDirectories();

  const {
    handleLocateProject,
    handleAutoLocateProject: handleAutoLocateProjectBase,
  } = useProjectAutoLocator({
    project: project ?? null,
    projectId,
  });

  // Wrap auto-locate to call refetchProject after success
  const handleAutoLocateProject = async () => {
    const result = await handleAutoLocateProjectBase();
    if (result) {
      refetchProject();
    }
  };

  const handleChangeProjectLocation = async () => {
    await handleLocateProject();
  };

  const handleResetProjectLocation = () => {
    removeProjectDirectory(projectId);
  };

  // Calculate primary DAW
  const primaryDAW = detectPrimaryDAW(
    latestVersionFiles.map(
      (f: {
        fileName: string;
        contentType: string;
        size: number;
        uploadedAt: string;
      }) => ({
        fileName: f.fileName,
        contentType: f.contentType,
        size: f.size,
        uploadedAt: f.uploadedAt,
      })
    )
  );

  // Open project in DAW hook
  const { handleOpenProject } = useOpenProjectInDAW({
    projectDirPath,
    latestVersionFiles,
    primaryDAW,
  });

  const value: ProjectDetailsContextValue = {
    projectId,
    project,
    projectLoading,
    projectError,
    refetchProject,
    projectDirHandle,
    projectDirPath,
    syncProjectDir,
    latestVersionFiles,
    primaryDAW,
    handleLocateProject,
    latestVersionLoading,
    latestVersionError,
    handleAutoLocateProject,
    handleChangeProjectLocation,
    handleResetProjectLocation,
    handleOpenProject,
    filesWithSyncRef,
    onProjectUpdated,
    selectedVersion,
    setSelectedVersion,
    latestVersionNumber,
  };

  return (
    <ProjectDetailsContext.Provider value={value}>
      {children}
    </ProjectDetailsContext.Provider>
  );
}

export function useProjectDetails() {
  const context = useContext(ProjectDetailsContext);
  if (!context) {
    throw new Error(
      'useProjectDetails must be used within ProjectDetailsProvider'
    );
  }
  return context;
}
